// The binary frame the native engine sends and receives (docs/design/native-app.md, decision
// 1), the same layout as crates/interplae-native/src/frame.rs: a u32 JSON length, the JSON, a
// u32 attachment count, then per attachment a u32 name length, the name, a u32 byte length,
// the bytes, all little-endian. A request's ArrayBuffer fields travel as attachments named by
// their field path; an answer's come back named by the result field they fill.

export interface Frame {
  json: unknown
  attachments: [string, ArrayBuffer][]
}

const encoder = new TextEncoder()
const decoder = new TextDecoder()

export function encodeFrame(frame: Frame): Uint8Array {
  const json = encoder.encode(JSON.stringify(frame.json))
  const names = frame.attachments.map(([name]) => encoder.encode(name))
  const length = 8 + json.byteLength + frame.attachments.reduce((n, [, bytes], i) => n + 8 + names[i]!.byteLength + bytes.byteLength, 0)
  const out = new Uint8Array(length)
  const view = new DataView(out.buffer)
  let at = 0
  const put = (bytes: Uint8Array) => {
    view.setUint32(at, bytes.byteLength, true)
    out.set(bytes, at + 4)
    at += 4 + bytes.byteLength
  }
  put(json)
  view.setUint32(at, frame.attachments.length, true)
  at += 4
  frame.attachments.forEach(([, bytes], i) => {
    put(names[i]!)
    put(new Uint8Array(bytes))
  })
  return out
}

export function decodeFrame(bytes: ArrayBuffer): Frame {
  const view = new DataView(bytes)
  let at = 0
  const damaged = (what: string) => new Error(`the frame is damaged: ${what}`)
  const length = (what: string) => {
    if (at + 4 > bytes.byteLength) throw damaged(what)
    const n = view.getUint32(at, true)
    at += 4
    return n
  }
  const piece = (what: string) => {
    const n = length(what)
    if (at + n > bytes.byteLength) throw damaged(what)
    const out = bytes.slice(at, at + n)
    at += n
    return out
  }
  const json = JSON.parse(decoder.decode(piece('the JSON'))) as unknown
  const count = length('the attachment count')
  const attachments: [string, ArrayBuffer][] = []
  for (let i = 0; i < count; i++) {
    const name = decoder.decode(piece("an attachment's name"))
    attachments.push([name, piece('an attachment')])
  }
  if (at !== bytes.byteLength) throw damaged('bytes follow the last attachment')
  return { json, attachments }
}

/// A request's JSON half and its bytes: every own field that is an ArrayBuffer, a Template's
/// bytes under `template.bytes`, with the JSON keeping `template: { name }`, and a New Project's
/// further files under `documents.N.bytes` and `documents.N.structure`, with the JSON keeping
/// `documents: [{ name }]`.
export function detach(request: object): Frame {
  const json: Record<string, unknown> = {}
  const attachments: [string, ArrayBuffer][] = []
  for (const [field, value] of Object.entries(request)) {
    if (value instanceof ArrayBuffer) attachments.push([field, value])
    else if (field === 'template' && value && typeof value === 'object' && 'bytes' in value && (value as { bytes: unknown }).bytes instanceof ArrayBuffer) {
      const { bytes, ...rest } = value as { bytes: ArrayBuffer }
      attachments.push(['template.bytes', bytes])
      json[field] = rest
    } else if (field === 'documents' && Array.isArray(value) && value.every(d => d && typeof d === 'object' && (d as { bytes?: unknown }).bytes instanceof ArrayBuffer)) {
      // A New Project's further files, each as the engine names it beside the request.
      json[field] = value.map((d: { name: string; bytes: ArrayBuffer; structure: ArrayBuffer | null }, n) => {
        attachments.push([`documents.${n}.bytes`, d.bytes])
        if (d.structure) attachments.push([`documents.${n}.structure`, d.structure])
        return { name: d.name }
      })
    } else json[field] = value
  }
  return { json, attachments }
}

/// An answer's result with each attachment put back under the field it fills. A null result
/// stays null, since nothing is attached to it.
export function attach(result: unknown, attachments: [string, ArrayBuffer][]): unknown {
  if (!attachments.length || result === null || typeof result !== 'object') return result
  const out = { ...(result as Record<string, unknown>) }
  for (const [name, bytes] of attachments) out[name] = bytes
  return out
}
